import {
    IconActivity,
    IconBell,
    IconCompany,
    IconGrid,
    IconHome,
    IconLoader,
    IconMessageCircle,
    IconModule1On1,
    IconModule360,
    IconModuleAutomations,
    IconModuleObjectives,
    IconModulePraise,
    IconModuleRequestFeedback,
    IconModuleReviews,
    IconSettings,
    IconSparkles,
    IconTeam,
} from 'featherico';
import { Observable } from 'ladda-observable';
import { negate, overSome, stubTrue } from 'lodash/fp';
import * as React from 'react';
import { Feature, UserCompanyDto } from '../../../../../types/CompanyDtos';
import NotificationCountDto from '../../../../../types/NotificationCountDto';
import { CurrentUserDto } from '../../../../../types/UserDtos';
import api from '../../../../api';
import * as R from '../../../../routes';
import { NAVIGATION } from '../../../intercom/intercom-data-targets';
import { isFeatureEnabled, isFeatureEnabledForUser } from '../../../services/Features';
import r from '../../../services/Replacement';
import { hasBasicHr, isAdmin, isAnyAdmin, isCycleAdminFor, isGuest, isHr } from '../../../services/User';
import { countAdminErrors } from '../../Admin/Overview/service';
import { showGeneralFeedbackWording } from '../../YourFeedback/services/feedback';

const featureStatus =
    (...features: Feature[]) =>
    (enabledFeatures: UserCompanyDto['enabledFeatures']) =>
        features.some(feature => enabledFeatures[feature]);

interface Link {
    title: string;
    route: string;
    alsoMatch?: string[];
    icon: React.ReactNode;
    /** For admins false will give faded-out appearance, for others will filter out the link */
    isFeatureEnabled?: (enabledFeatures: UserCompanyDto['enabledFeatures']) => boolean;
    /** Determine whether the link is visible in the navigation */
    isAvailableTo: (currentUser: CurrentUserDto) => boolean;
    notifier?: null | Notifier;
    titleClass?: string;
    trackingLabel: string;
    testId: string; // These IDs are separate from tracking and intercom IDs to avoid tests breaking when they change.
    /** When set, admins will see a pillbox beside the link.  */
    getStatusLabel?: (currentUser: CurrentUserDto) => Promise<string | undefined>;
    dataIntercomTarget?: string;
    onClick?: (e: React.MouseEvent) => void;
    openInNewTab?: boolean;
}

interface Notifier<T = any> {
    observe: () => Observable<T>;
    toNotification: (r: T) => { count: number; color: string };
}

interface Group {
    className?: string;
    links: Link[];
}

export const employeeLinks = (highlightComingSoon: boolean): Group[] => [
    {
        className: '',
        links: [
            {
                title: 'Home',
                route: R.BASE + R.DASHBOARD,
                icon: <IconHome />,
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'home',
                testId: 'home',
            },
            {
                title: 'Inbox',
                route: R.BASE + R.INBOX,
                icon: <IconBell />,
                isAvailableTo: user => !isGuest(user) && isFeatureEnabledForUser('FEATURE_inboxPage', user),
                notifier: {
                    observe: () => api.notificationsCount.getCount.createObservable(),
                    toNotification: ({ count }: NotificationCountDto) => ({
                        count,
                        color: 'RED',
                    }),
                } as Notifier<NotificationCountDto>,
                trackingLabel: 'inbox',
                testId: 'inbox',
            },
            {
                title: r('praise.menu.title'),
                route: R.BASE + R.PRAISE_STREAM,
                icon: <IconModulePraise />,
                isFeatureEnabled: featureStatus('praise'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'praise',
                testId: 'praise',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_PRAISE,
            },
            {
                title: r('meeting.name.plural'),
                route: R.BASE + R.MEETING_OVERVIEW,
                alsoMatch: [R.BASE + R.MEETING_DEDICATED],
                icon: <IconModule1On1 />,
                isFeatureEnabled: featureStatus('meetings'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'meetings',
                testId: 'meetings',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_MEETINGS,
            },
        ],
    },
    {
        links: [
            {
                title: r('surveys.plural.big'),
                route: R.BASE + R.YOUR_SURVEYS,
                icon: <IconActivity />,
                isFeatureEnabled: f => f.surveys,
                isAvailableTo: user => (user.isGuest ? false : isFeatureEnabledForUser('surveys', user) || isHr(user)),
                notifier: null,
                trackingLabel: 'surveys',
                testId: 'surveys',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_SURVEYS,
            },
            {
                title: r('objective.plural.title'),
                route: R.BASE + R.OBJECTIVES,
                icon: <IconModuleObjectives />,
                isFeatureEnabled: featureStatus('objective'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'objectives',
                testId: 'objectives',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_OBJECTIVES,
            },
            {
                title: showGeneralFeedbackWording ? r('fr.menu.unified.title') : r('fr.menu.title'),
                route: R.BASE + R.FEEDBACK_USER,
                icon: <IconModule360 />,
                isFeatureEnabled: featureStatus('feedback', 'requestFeedback'),
                isAvailableTo: stubTrue,
                notifier: null,
                trackingLabel: '360s',
                testId: '360s',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_FEEDBACK,
            },
            {
                title: r('pr.menu.title'),
                route: R.BASE + R.REVIEWS,
                alsoMatch: ['/appraisalView', '/appraisalDetails'],
                icon: <IconModuleReviews />,
                isFeatureEnabled: featureStatus('review'),
                isAvailableTo: negate(isGuest),
                notifier: null,
                trackingLabel: 'reviews',
                testId: 'reviews',
                dataIntercomTarget: NAVIGATION.EMPLOYEE_REVIEWS,
            },
        ],
    },
    {
        className: '',
        links: [
            {
                title: 'Automations',
                route: R.BASE + R.EMPLOYEE_AUTOMATIONS,
                icon: <IconModuleAutomations />,
                isFeatureEnabled: featureStatus('meetings'), // TODO: When we add more automations this needs to check if any actions/triggers are available
                isAvailableTo: u =>
                    !isGuest(u) &&
                    isFeatureEnabledForUser('FEATURE_automationsEmployee', u) &&
                    !isFeatureEnabledForUser('FEATURE_adminNavigationRefresh', u),
                notifier: null,
                trackingLabel: 'employeeAutomations',
                testId: 'employeeAutomations',
            },
            {
                title: 'People',
                route: R.BASE + R.PEOPLE,
                icon: <IconTeam />,
                isAvailableTo: u => !isGuest(u),
                notifier: null,
                trackingLabel: 'people',
                testId: 'people',
            },
            {
                title: 'AI Coach',
                route: R.BASE + R.AI_COPILOT,
                icon: <IconSparkles />,
                isAvailableTo: u =>
                    isFeatureEnabled('ai-features') &&
                    isFeatureEnabled('FEATURE_aiCopilot') &&
                    ((hasBasicHr(u) && isFeatureEnabled('FEATURE_adminNavigationRefresh')) || u.isSmallImprovements),
                trackingLabel: 'employeeAiCopilot',
                testId: 'employeeAiCopilot',
            },
            {
                title: 'Settings',
                route: R.BASE + R.SETTINGS,
                icon: <IconSettings />,
                isAvailableTo: u => isAnyAdmin(u) && isFeatureEnabledForUser('FEATURE_adminNavigationRefresh', u),
                notifier: {
                    observe: () => api.admin.getSettingsLinks.createObservable(true),
                    toNotification: links => ({ count: countAdminErrors(links), color: 'RED' }),
                },
                trackingLabel: 'adminSettings',
                testId: 'adminSettings',
            },
            {
                title: 'Coming Soon',
                route: R.BASE + R.COMING_SOON,
                icon: <IconLoader />,
                isAvailableTo: u =>
                    hasBasicHr(u) &&
                    isFeatureEnabledForUser('FEATURE_earlyAccessProgram', u) &&
                    isFeatureEnabledForUser('FEATURE_adminNavigationRefresh', u),
                titleClass: highlightComingSoon ? 'coming-soon' : '',
                trackingLabel: 'adminEarlyAccessProgram',
                testId: 'adminEarlyAccessProgram',
            },
        ],
    },
];

export const adminLinks = (highlightComingSoon: boolean): Group[] => [
    {
        links: [
            {
                title: 'Home',
                route: R.BASE + R.ADMIN_DASHBOARD,
                icon: <IconHome />,
                isAvailableTo: hasBasicHr,
                notifier: null,
                trackingLabel: 'adminDashboard',
                testId: 'adminDashboard',
            },
            {
                title: r('praise.singular'),
                route: R.BASE + R.ADMIN_PRAISE,
                icon: <IconModulePraise />,
                isFeatureEnabled: featureStatus('praise'),
                isAvailableTo: isAnyAdmin,
                notifier: null,
                trackingLabel: 'adminPraise',
                testId: 'adminPraise',
                dataIntercomTarget: NAVIGATION.ADMIN_PRAISE,
            },
            {
                title: r('meeting.name.plural'),
                route: R.BASE + R.ADMIN_MEETINGS,
                icon: <IconModule1On1 />,
                isFeatureEnabled: featureStatus('meetings'),
                isAvailableTo: hasBasicHr,
                notifier: null,
                trackingLabel: 'adminMeetings',
                testId: 'adminMeetings',
                dataIntercomTarget: NAVIGATION.ADMIN_MEETINGS,
            },
            {
                title: `${r('surveys.plural.big')}`,
                route: R.BASE + R.ADMIN_SURVEYS,
                icon: <IconActivity />,
                isFeatureEnabled: f => f.surveys,
                isAvailableTo: hasBasicHr,
                notifier: null,
                trackingLabel: 'adminSurveys',
                testId: 'adminSurveys',
                dataIntercomTarget: NAVIGATION.ADMIN_SURVEYS,
            },
            {
                title: r('objective.plural.title'),
                route: R.BASE + R.ADMIN_OBJECTIVES_SELECT_CYCLE,
                icon: <IconModuleObjectives />,
                isFeatureEnabled: featureStatus('objective'),
                isAvailableTo: user => hasBasicHr(user) || isCycleAdminFor(user, 'OBJECTIVES'),
                notifier: null,
                trackingLabel: 'adminObjectives',
                testId: 'adminObjectives',
                dataIntercomTarget: NAVIGATION.ADMIN_OBJECTIVES,
            },
            {
                title: r('fr.menu.title'),
                route: R.BASE + R.ADMIN_FEEDBACK_SELECT_CYCLE,
                icon: <IconModule360 />,
                isFeatureEnabled: featureStatus('feedback'),
                isAvailableTo: user => hasBasicHr(user) || isCycleAdminFor(user, '360_FEEDBACK'),
                notifier: null,
                trackingLabel: 'admin360s',
                testId: 'admin360s',
                dataIntercomTarget: NAVIGATION.ADMIN_360_FEEDBACK,
            },
            {
                title: isFeatureEnabled('FEATURE_anytimeFeedbackGive') ? r('anyfb.name') : 'Feedback Requests',
                route: R.BASE + R.ADMIN_FEEDBACK_REQUESTED,
                icon: <IconModuleRequestFeedback />,
                isFeatureEnabled: featureStatus('requestFeedback'),
                isAvailableTo: hasBasicHr,
                notifier: null,
                trackingLabel: 'adminRequestedFeedback',
                testId: 'adminRequestedFeedback',
                dataIntercomTarget: NAVIGATION.ADMIN_ANYTIME_FEEDBACK,
            },
            {
                title: r('pr.menu.title'),
                route: R.BASE + R.ADMIN_REVIEWS_SELECT_CYCLE,
                icon: <IconModuleReviews />,
                isFeatureEnabled: featureStatus('review'),
                isAvailableTo: user => hasBasicHr(user) || isCycleAdminFor(user, 'REVIEWS'),
                notifier: null,
                trackingLabel: 'adminReviews',
                testId: 'adminReviews',
                dataIntercomTarget: NAVIGATION.ADMIN_REVIEWS,
            },
            {
                title: r('messages.menu.title'),
                route: R.BASE + R.ADMIN_MESSAGES,
                icon: <IconMessageCircle />,
                isFeatureEnabled: featureStatus('message'),
                isAvailableTo: u => hasBasicHr(u) && isFeatureEnabledForUser('message', u),
                notifier: null,
                trackingLabel: 'adminMessages',
                testId: 'adminMessages',
                dataIntercomTarget: NAVIGATION.ADMIN_MESSAGES,
            },
            {
                title: 'Automations',
                route: R.BASE + R.HR_AUTOMATIONS,
                icon: <IconModuleAutomations />,
                isAvailableTo: u => isFeatureEnabledForUser('FEATURE_automationsHrMenu', u) && hasBasicHr(u),
                notifier: null,
                trackingLabel: 'adminAutomations',
                testId: 'adminAutomations',
            },
        ],
    },
    {
        className: '',
        links: [
            {
                title: 'Directory',
                route: R.BASE + R.ADMIN_USERS_DIRECTORY,
                icon: <IconCompany />,
                isAvailableTo: isAnyAdmin,
                notifier: null,
                trackingLabel: 'adminDirectory',
                testId: 'adminDirectory',
            },
            {
                title: 'Integrations',
                route: R.BASE + R.INTEGRATIONS,
                icon: <IconGrid />,
                isAvailableTo: overSome([isHr, isAdmin]),
                notifier: {
                    observe: api.admin.getIntegrationsLinks.createObservable,
                    toNotification: links => ({ count: countAdminErrors(links), color: 'RED' }),
                },
                trackingLabel: 'adminIntegrations',
                testId: 'adminIntegrations',
            },
            {
                title: 'AI Coach',
                route: R.BASE + R.ADMIN_AI_COPILOT,
                icon: <IconSparkles />,
                isAvailableTo: u =>
                    hasBasicHr(u) && isFeatureEnabled('ai-features') && isFeatureEnabled('FEATURE_aiCopilot'),
                trackingLabel: 'adminAiCopilot',
                testId: 'adminAiCopilot',
            },
            {
                title: 'Settings',
                route: R.BASE + R.ADMIN_OVERVIEW,
                alsoMatch: ['/admin'],
                icon: <IconSettings />,
                isAvailableTo: isAnyAdmin,
                notifier: {
                    observe: api.admin.getSettingsLinks.createObservable,
                    toNotification: links => ({ count: countAdminErrors(links), color: 'RED' }),
                },
                trackingLabel: 'adminSettings',
                testId: 'adminSettings',
            },
            {
                title: 'Coming Soon',
                route: R.BASE + R.ADMIN_EARLY_ACCESS,
                icon: <IconLoader />,
                isAvailableTo: u => {
                    return hasBasicHr(u) && isFeatureEnabledForUser('FEATURE_earlyAccessProgram', u);
                },
                titleClass: highlightComingSoon ? 'coming-soon' : '',
                trackingLabel: 'adminEarlyAccessProgram',
                testId: 'adminEarlyAccessProgram',
            },
        ],
    },
];
